import axios, { type AxiosResponse } from "axios";
import { gatewayApiInstance } from "../../api/axios/api-instances";
import { getAppKey } from "../lib/helpers";
import { appSession, type AppSession } from "../lib/storage";
import { InvalidSessionError, MissingRefreshTokenError } from "./auth-errors";
import { announceAutomaticLogout } from "./automatic-logout";

export type RefreshReason = string;

export type RefreshAccessTokenInput = {
  storeKey?: string;
  reason: RefreshReason;
};

type RefreshTokenResponse = {
  result: Pick<AppSession, "access_token" | "refresh_token">;
};

const REFRESH_TIMEOUT_MS = 10_000;

let refreshInFlight: Promise<AxiosResponse<RefreshTokenResponse>> | undefined;

const getDefinitivelyInvalidStatus = (
  error: unknown,
): 400 | 401 | 403 | undefined => {
  if (!axios.isAxiosError(error)) return undefined;

  const status = error.response?.status;
  return status === 400 || status === 401 || status === 403
    ? status
    : undefined;
};

const requestNewTokens = async ({
  storeKey,
  reason,
}: RefreshAccessTokenInput) => {
  const session = appSession.getItem();

  if (!session?.refresh_token) {
    announceAutomaticLogout({
      reason: "credentials_missing",
      operation: reason,
    });
    throw new MissingRefreshTokenError();
  }

  try {
    const response = await gatewayApiInstance.post<RefreshTokenResponse>(
      "/gateway/staff/refresh_token",
      {
        refresh_token: session.refresh_token,
        ...(storeKey
          ? {
              shop_key: storeKey,
              app_key: getAppKey(),
            }
          : {}),
      },
      {
        headers: { Authorization: "" },
        authRecovery: { skip: true },
        // Every 401 replay awaits this single in-flight promise, so a refresh
        // that never settles deadlocks every authenticated request in the app
        // until the page is reloaded. Keep it strictly shorter than the shared
        // request timeout so the singleton below always clears.
        timeout: REFRESH_TIMEOUT_MS,
      }
    );

    appSession.setItem(response.data.result);
    return response;
  } catch (error) {
    // A response-less failure (including CORS), throttling, and server outages do
    // not prove the reusable refresh token is invalid. Preserve it for recovery.
    const invalidStatus = getDefinitivelyInvalidStatus(error);
    if (invalidStatus && axios.isAxiosError(error)) {
      announceAutomaticLogout({
        reason: "refresh_rejected",
        operation: reason,
        status: invalidStatus,
      });
      appSession.removeItem();
      throw new InvalidSessionError(invalidStatus, error);
    }

    throw error;
  }
};

export const refreshAccessToken = (input: RefreshAccessTokenInput) => {
  if (!refreshInFlight) {
    refreshInFlight = requestNewTokens(input).finally(() => {
      refreshInFlight = undefined;
    });
  }

  return refreshInFlight;
};
